import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";
import { BrandMark, LogoOnly } from "@/components/Brand";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-zinc-950 text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dense opacity-10" />
        <Link to="/" className="relative flex items-center gap-3" data-testid="login-brand">
          <div className="bg-white p-1">
            <LogoOnly size={28} />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-display font-black tracking-tight text-lg">ARISSAI</span>
            <span className="text-[10px] tracking-[0.18em] uppercase font-semibold text-white/60">Project Intelligence</span>
          </div>
        </Link>
        <div className="relative">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500">// Welcome back</div>
          <h1 className="font-display font-black tracking-tighter text-5xl mt-4">
            Six lenses. <br />Zero blind spots.
          </h1>
          <p className="text-zinc-400 mt-4 max-w-md">
            Sign in to run your projects through the AI lens array. Validate assumptions, forecast risks, and ship decisions with conviction.
          </p>
        </div>
        <div className="relative text-[11px] tracking-[0.2em] uppercase font-bold text-zinc-500 font-mono">
          © 2026 ARISSAI
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-zinc-100">
        <form onSubmit={submit} className="w-full max-w-md bg-white border border-zinc-200 p-10" data-testid="login-form">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500">Sign in</div>
          <h2 className="font-display font-black tracking-tighter text-3xl mt-2">Access ARISSAI</h2>

          <label className="block mt-8 text-xs tracking-[0.2em] uppercase font-bold text-zinc-600">Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full mt-2 h-12 px-4 border border-zinc-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:border-zinc-950"
            data-testid="login-email-input"
            placeholder="you@company.com"
          />

          <label className="block mt-5 text-xs tracking-[0.2em] uppercase font-bold text-zinc-600">Password</label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full mt-2 h-12 px-4 border border-zinc-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:border-zinc-950"
            data-testid="login-password-input"
            placeholder="••••••••"
          />

          <button
            type="submit"
            disabled={submitting}
            className="mt-8 w-full h-12 bg-zinc-950 text-white text-sm font-semibold hover:bg-zinc-800 transition-colors disabled:opacity-50 inline-flex items-center justify-center"
            data-testid="login-submit-button"
          >
            {submitting ? "Signing in..." : <>Sign in <ArrowRight size={16} weight="bold" className="ml-2" /></>}
          </button>

          <div className="text-sm text-zinc-600 mt-6">
            No account?{" "}
            <Link to="/signup" className="font-semibold text-zinc-950 hover:underline" data-testid="login-link-signup">
              Create one
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
